import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useMemo,
} from "react";
import Organization from "../models/Organization";
import { useSearchParams } from "react-router-dom";
import {
  getOrganizationWhiteLabelLogoUrl,
  resolveSidebarLogoUrl,
} from "../helpers/whiteLabelAssistantName";

const OrganizationContext = createContext();

export const useOrganization = () => useContext(OrganizationContext);

const BRAND_VARIABLES = [
  "--primary-color",
  "--first-button-color",
  "--first-button-hover-color",
  "--second-button-color",
  "--selected-color",
  "--background-label-color",
  "--ai-gradient-middle",
  "--ai-gradient-end",
  "--title-gradient",
];

const isValidHexColor = (value) =>
  typeof value === "string" && /^#[0-9a-fA-F]{6}$/.test(value.trim());

const resolveBrandColor = (...values) => {
  const match = values.find(isValidHexColor);
  return match ? match.trim() : "";
};

const resolveCssValue = (value) => (typeof value === "string" && value.trim() ? value.trim() : "");
const resolveStringValue = (value) =>
  typeof value === "string" && value.trim() ? value.trim() : "";

// Same shape the client relays and the extension's sidebar.js saves, so the floating launcher re-brands on org switches made here.
const postSelectedOrganizationBrandingToParent = (organization) => {
  if (typeof window === "undefined" || !window.parent || window.parent === window) return;

  const settings = organization.whiteLabelSettings || {};
  window.parent.postMessage(
    {
      key: "selected-org-branding",
      value: {
        organizationId: organization.id,
        appName: resolveStringValue(settings.appName),
        assistantName: resolveStringValue(settings.assistantName),
        primaryColor: resolveBrandColor(settings.primaryColor, organization.primaryColor),
        logoUrl: getOrganizationWhiteLabelLogoUrl(organization),
      },
    },
    "*",
  );
};

const applyOrganizationBrandCssVariables = (organization) => {
  if (typeof document === "undefined") return;

  const root = document.documentElement;
  const settings = organization?.whiteLabelSettings || {};
  const primaryColor = resolveBrandColor(settings.primaryColor, organization?.primaryColor);
  const secondaryColor = resolveBrandColor(settings.secondaryColor);
  const accentColor = resolveBrandColor(settings.accentColor);

  if (!primaryColor && !secondaryColor && !accentColor) {
    BRAND_VARIABLES.forEach((variable) => root.style.removeProperty(variable));
    return;
  }

  if (primaryColor) {
    root.style.setProperty("--primary-color", primaryColor);
    root.style.setProperty("--first-button-color", primaryColor);
    root.style.setProperty(
      "--first-button-hover-color",
      `color-mix(in srgb, ${primaryColor} 86%, black)`,
    );
    root.style.setProperty("--second-button-color", "#ffffff");
    root.style.setProperty(
      "--selected-color",
      `color-mix(in srgb, ${secondaryColor || primaryColor} 14%, white)`,
    );
    root.style.setProperty(
      "--background-label-color",
      `color-mix(in srgb, ${secondaryColor || primaryColor} 16%, transparent)`,
    );
    root.style.setProperty(
      "--ai-gradient-middle",
      `color-mix(in srgb, ${secondaryColor || primaryColor} 72%, transparent)`,
    );
    root.style.setProperty("--ai-gradient-end", accentColor || secondaryColor || primaryColor);
    root.style.setProperty(
      "--title-gradient",
      `linear-gradient(90deg, ${primaryColor} 0%, ${secondaryColor || primaryColor} 52%, ${
        accentColor || secondaryColor || primaryColor
      } 100%)`,
    );
  }
};

export const applyQueryBrandCssVariables = (brandSettings) => {
  if (typeof document === "undefined") return;

  const root = document.documentElement;
  const primaryColor =
    resolveBrandColor(brandSettings.primaryColor) ||
    resolveCssValue(brandSettings.primaryColor);
  if (!primaryColor) return;

  root.style.setProperty("--primary-color", primaryColor);
  root.style.setProperty("--first-button-color", primaryColor);
  root.style.setProperty(
    "--first-button-hover-color",
    `color-mix(in srgb, ${primaryColor} 86%, black)`,
  );
  root.style.setProperty("--second-button-color", "#ffffff");
  root.style.setProperty(
    "--selected-color",
    brandSettings.selectedColor || `color-mix(in srgb, ${primaryColor} 14%, white)`,
  );
  root.style.setProperty(
    "--background-label-color",
    brandSettings.labelColor || `color-mix(in srgb, ${primaryColor} 16%, transparent)`,
  );
  root.style.setProperty(
    "--ai-gradient-middle",
    brandSettings.gradientMiddle || `color-mix(in srgb, ${primaryColor} 72%, transparent)`,
  );
  root.style.setProperty("--ai-gradient-end", brandSettings.gradientEnd || primaryColor);
  root.style.setProperty(
    "--title-gradient",
    `linear-gradient(90deg, ${primaryColor} 0%, ${
      brandSettings.gradientMiddle || primaryColor
    } 52%, ${brandSettings.gradientEnd || primaryColor} 100%)`,
  );
};

export const OrganizationProvider = ({ children }) => {
  const [params] = useSearchParams();
  const organizationIdQueryParam = params.get("organizationId");
  const dataQueryParam = params.get("data");
  const uidQueryParam = params.get("uid");
  const modeParam = params.get("mode"); // "documentPortal" when embedded in borrower portal
  const queryBrandSettings = useMemo(
    () => ({
      primaryColor: resolveBrandColor(params.get("brandPrimaryColor")),
      selectedColor: resolveCssValue(params.get("brandSelectedColor")),
      labelColor: resolveCssValue(params.get("brandLabelColor")),
      gradientMiddle: resolveCssValue(params.get("brandGradientMiddle")),
      gradientEnd: resolveCssValue(params.get("brandGradientEnd")),
      logoUrl: resolveSidebarLogoUrl(params.get("brandLogoUrl")),
      appName: resolveStringValue(params.get("brandAppName")),
      assistantName: resolveStringValue(params.get("brandAssistantName")),
      faviconUrl: resolveStringValue(params.get("brandFaviconUrl")),
      brandingSlug: resolveStringValue(params.get("brandingSlug")),
    }),
    [params],
  );

  const data = useMemo(() => {
    return dataQueryParam ? JSON.parse(dataQueryParam) : {};
  }, [dataQueryParam]);

  const uid = uidQueryParam || data?.uid || null;

  const [selectedOrganizationId, setSelectedOrganizationId] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  // True once the organization lookup has settled, including when it found
  // none or was skipped. Consumers that must not act on a half-known
  // organization (announcement audience targeting) wait for this rather than
  // for a truthy id, which never arrives for a user without organizations.
  const [organizationsFetched, setOrganizationsFetched] = useState(false);

  useEffect(() => {
    // Document portal sessions use a synthetic "borrower-<id>" uid scoped to
    // the borrower (see RightSidebar.tsx), which never matches a real user or
    // organization record. organizationId already arrives via query param for
    // that flow, so skip the lookup instead of generating a guaranteed 404.
    if (uid && modeParam !== "documentPortal") {
      Organization.getOrganizations(uid)
        .then((organizations) => {
          setOrganizations(organizations || []);

          if (organizations.length) {
            setSelectedOrganizationId((currentOrganizationId) => {
              if (currentOrganizationId) return currentOrganizationId;
              return organizationIdQueryParam || organizations[0].id;
            });
          }
        })
        .catch((error) => {
          // Leave the list empty rather than rejecting into the void; the
          // sidebar still works without organization data.
          console.warn(
            "[OrganizationContext] Failed to load organizations:",
            error?.message || error,
          );
        })
        .finally(() => setOrganizationsFetched(true));
      return;
    }

    // No uid, or a document-portal session whose organization arrives by query
    // param: there is no lookup to wait for.
    setOrganizationsFetched(true);
  }, [uid, organizationIdQueryParam, modeParam]);

  useEffect(() => {
    if (organizationIdQueryParam) {
      setSelectedOrganizationId(organizationIdQueryParam);
    }
  }, [organizationIdQueryParam]);

  const selectedOrganization = useMemo(
    () =>
      organizations.find((organization) => organization.id === selectedOrganizationId) ||
      null,
    [organizations, selectedOrganizationId],
  );

  useEffect(() => {
    // URL params from the parent client carry the host app's live theme
    // (data-theme presets + org overrides). Prefer them over async org fetch
    // so sidebar UI like Chat with your docs matches the main app colors.
    if (isValidHexColor(queryBrandSettings.primaryColor)) {
      applyQueryBrandCssVariables(queryBrandSettings);
      return;
    }

    const selectedOrganizationSettings = selectedOrganization?.whiteLabelSettings || {};
    const hasSelectedOrganizationBrand =
      selectedOrganization &&
      (isValidHexColor(selectedOrganizationSettings.primaryColor) ||
        isValidHexColor(selectedOrganization?.primaryColor) ||
        isValidHexColor(selectedOrganizationSettings.secondaryColor) ||
        isValidHexColor(selectedOrganizationSettings.accentColor));

    if (hasSelectedOrganizationBrand) {
      applyOrganizationBrandCssVariables(selectedOrganization);
      return;
    }

    applyQueryBrandCssVariables(queryBrandSettings);
  }, [selectedOrganization, queryBrandSettings]);

  useEffect(() => {
    if (selectedOrganization) {
      postSelectedOrganizationBrandingToParent(selectedOrganization);
    }
  }, [selectedOrganization]);

  const value = {
    selectedOrganizationId,
    setSelectedOrganizationId,
    selectedOrganization,
    queryBrandSettings,
    organizations,
    organizationsFetched,
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};

export default OrganizationContext;
